import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  getTeam,
  updateTeam,
  addMember,
  removeMember,
  type Team,
  type UpdateTeamData,
} from '../../api/teams';
import { Modal } from '../common/Modal';
import { ConfirmDialog } from '../common/ConfirmDialog';

export function TeamDetailView() {
  const { teamId } = useParams<{ teamId: string }>();
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [newMember, setNewMember] = useState('');

  const fetchData = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getTeam(teamId, token);
      setTeam(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [teamId, getJwtToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = async (data: UpdateTeamData) => {
    if (!teamId) return;
    const token = await getJwtToken();
    if (!token) return;
    await updateTeam(teamId, data, token);
    setShowEditModal(false);
    await fetchData();
  };

  const handleAddMember = async () => {
    if (!teamId || !newMember.trim()) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await addMember(teamId, newMember.trim(), token);
      setNewMember('');
      setShowAddMemberModal(false);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    }
  };

  const handleRemoveMember = async () => {
    if (!teamId || !memberToRemove) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await removeMember(teamId, memberToRemove, token);
      setMemberToRemove(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
      setMemberToRemove(null);
    }
  };

  if (loading && !team) {
    return <div className="view__empty">Loading team details...</div>;
  }

  if (!team) {
    return (
      <div className="view__empty">
        <p>Team not found.</p>
        <button className="btn btn--secondary" onClick={() => navigate('/admin/teams')} type="button">
          Back to Teams
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">{team.name}</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowEditModal(true)}
            type="button"
          >
            Edit
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => navigate('/admin/teams')}
            type="button"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      <section className="view__section">
        <h2 className="view__section-title">Team Information</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">Team ID</span>
            <span className="settings-card__value"><code>{team.teamId}</code></span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Name</span>
            <span className="settings-card__value">{team.name}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Description</span>
            <span className="settings-card__value">{team.description || '--'}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Max Budget</span>
            <span className="settings-card__value">
              {team.maxBudget != null ? `$${team.maxBudget.toFixed(2)}` : 'Unlimited'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Created</span>
            <span className="settings-card__value">{new Date(team.createdAt).toLocaleString()}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Updated</span>
            <span className="settings-card__value">{new Date(team.updatedAt).toLocaleString()}</span>
          </div>
        </div>
      </section>

      <section className="view__section">
        <div className="view__header" style={{ marginBottom: 0 }}>
          <h2 className="view__section-title">Members ({team.members?.length ?? 0})</h2>
          <div className="view__actions">
            <button
              className="btn btn--primary"
              onClick={() => setShowAddMemberModal(true)}
              type="button"
            >
              + Add Member
            </button>
          </div>
        </div>

        {team.members && team.members.length > 0 ? (
          <div className="data-table__wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="data-table__th">Username</th>
                  <th className="data-table__th" style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {team.members.map((member) => (
                  <tr key={member} className="data-table__tr">
                    <td className="data-table__td">{member}</td>
                    <td className="data-table__td">
                      <button
                        className="btn btn--danger btn--sm"
                        onClick={() => setMemberToRemove(member)}
                        type="button"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="data-table__empty">No members yet. Click 'Add Member' to add one.</div>
        )}
      </section>

      {/* Edit Team Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Team"
        wide
      >
        <EditTeamForm
          team={team}
          onSubmit={handleUpdate}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* Add Member Modal */}
      <Modal
        isOpen={showAddMemberModal}
        onClose={() => setShowAddMemberModal(false)}
        title="Add Member"
      >
        <div>
          <div className="form-group">
            <label className="form-group__label">Username</label>
            <input
              className="form-group__input"
              value={newMember}
              onChange={(e) => setNewMember(e.target.value)}
              placeholder="Enter username"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddMember();
                }
              }}
            />
          </div>
          <div className="modal__actions">
            <button
              type="button"
              className="modal__cancel"
              onClick={() => setShowAddMemberModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="modal__submit"
              onClick={handleAddMember}
              disabled={!newMember.trim()}
            >
              Add Member
            </button>
          </div>
        </div>
      </Modal>

      {/* Remove Member Confirm */}
      <ConfirmDialog
        isOpen={memberToRemove !== null}
        title="Remove Member"
        message={`Are you sure you want to remove "${memberToRemove}" from this team?`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleRemoveMember}
        onCancel={() => setMemberToRemove(null)}
      />
    </div>
  );
}

/* ---------- inline form for editing a team ---------- */

interface EditTeamFormProps {
  team: Team;
  onSubmit: (data: UpdateTeamData) => Promise<void>;
  onCancel: () => void;
}

function EditTeamForm({ team, onSubmit, onCancel }: EditTeamFormProps) {
  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description);
  const [maxBudget, setMaxBudget] = useState(team.maxBudget?.toString() ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name,
        description,
        maxBudget: maxBudget ? Number(maxBudget) : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update team');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="alert alert--error" style={{ marginBottom: 12 }}>
          <span>{error}</span>
        </div>
      )}

      <div className="form-group">
        <label className="form-group__label">Name</label>
        <input
          className="form-group__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label className="form-group__label">Description</label>
        <input
          className="form-group__input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label className="form-group__label">Max Budget ($)</label>
        <input
          className="form-group__input"
          type="number"
          step="0.01"
          min="0"
          value={maxBudget}
          onChange={(e) => setMaxBudget(e.target.value)}
          placeholder="Leave empty for unlimited"
        />
      </div>

      <div className="modal__actions">
        <button type="button" className="modal__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="modal__submit" disabled={submitting}>
          {submitting ? 'Saving...' : 'Update Team'}
        </button>
      </div>
    </form>
  );
}
